// 推廣活動（Lead Campaign）後台 — 對標食肆優惠三 TAB 模式：
// TAB1 活動管理（CRUD/toggle/落地 link 複製）｜TAB2 領取名單（郵寄流轉＋label 列印/CSV）｜TAB3 成效報表（漏斗＋郵寄＋CAC/LTV）
// Label 規格：A4 14 上（99.1×38.1mm，2×7）——設計為「信封上的邀請卡」：
//   收件地址（功能）＋收件人專屬推薦 QR（閉環：朋友掃碼落單＝收件人賺回贈）＋影相分享誘因。
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Megaphone, Download, Printer, RefreshCw, Plus, Edit2, Trash2, Copy, Users, BarChart3 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import AdminLayout from '../components/AdminLayout';
import { API_BASE, apiFetch } from '../lib/api';
import { SITE_NAME } from '../lib/brand';

interface Campaign {
  id: number;
  key: string;
  name: string;
  headline: string;
  subline: string;
  benefits: string[];
  promoCode: string;
  shortCode: string;
  giftProductId: number | null;
  adSpendCents: number | null;
  liveAt: number | null;
  reminderDone: number;
  isActive: number;
  sortOrder: number;
  claimCount: number;
}
interface Claim {
  id: number;
  phone: string;
  name: string;
  address: string;
  region: string;
  referral_code: string | null;
  status: string;
  source: string;
  campaign_id: number | null;
  created_referrer: number;
  reminder_sent: number;
  created_at: number;
}
interface StatsCommerce {
  buyers: number;
  orders: number;
  revenueCents: number;
  revenuePerBuyerCents: number;
  cacCents: number | null;
  adSpendCents: number;
  leadToBuyerRate: string;
}
interface Stats {
  funnel: { views: number; formStarts: number; submitsOk: number; submitsDupPhone: number; submitsBlocked: number; claims: number; newMembers: number };
  mailing: Record<string, number>;
  commerce?: StatsCommerce;
}

const STATUS_LABELS: Record<string, string> = { claimed: '待寄出', sent: '已寄出', delivered: '已送達' };
const emptyForm = { key: '', name: '', headline: '', subline: '', benefitsText: '', promoCode: '', sortOrder: '0', isActive: true, giftProductId: '' as string | number, adSpendHkd: '', liveAt: '' };

// epoch ↔ datetime-local（瀏覽器本地時區；後台用戶＝HKT）
const epochToInput = (epoch: number | null | undefined): string => {
  if (!epoch) return '';
  const d = new Date(epoch * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const inputToEpoch = (v: string): number | null => {
  if (!v) return null;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? Math.round(t / 1000) : null;
};

const AdminGodfather: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'campaigns' | 'claims' | 'stats'>('campaigns');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [claims, setClaims] = useState<Claim[]>([]);
  const [stats, setStats] = useState<Record<number, Stats>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [claimsCampaignFilter, setClaimsCampaignFilter] = useState<number | 'all'>('all');
  const [products, setProducts] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    fetchCampaigns();
  }, [navigate]);

  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(''), 2500); return () => clearTimeout(t); }
  }, [success]);

  // 切到名單/報表 TAB 時補抓數據（mount 時 tab=campaigns，claims 不會在首載抓取）
  useEffect(() => {
    if ((tab === 'claims' || tab === 'stats') && campaigns.length) {
      fetchClaims(campaigns);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const authHeaders = (): Record<string, string> => {
    const token = localStorage.getItem('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/public/admin/lead-campaigns');
      if (res.status === 401) { localStorage.removeItem('admin_token'); navigate('/admin'); return; }
      const data = await res.json();
      const rows = Array.isArray(data) ? data : data.data || [];
      setCampaigns(rows);
      // 贈品掛鉤產品選單（靜默失敗不影響主功能）
      apiFetch('/api/public/admin/products').then((r) => r.json()).then((pd) => {
        const list = Array.isArray(pd) ? pd : pd.data || [];
        setProducts(list.map((p: any) => ({ id: p.id, name: p.name })));
      }).catch(() => {});
      if (rows.length && (tab === 'claims' || tab === 'stats')) await fetchClaims(rows);
    } catch (e: any) { setError(e.message || '載入失敗'); }
    finally { setLoading(false); }
  };

  const fetchClaims = async (list?: Campaign[]) => {
    const res = await apiFetch('/api/public/admin/godfather/claims');
    const data = await res.json();
    setClaims(Array.isArray(data) ? data : []);
    // 成效報表數據按活動抓取
    const ids = (list || campaigns).map((c) => c.id);
    const entries = await Promise.all(ids.map(async (id) => {
      const r = await apiFetch(`/api/public/admin/lead-campaigns/${id}/stats`);
      const d = await r.json().catch(() => null);
      return [id, d && (d.funnel ? d : d.data)] as [number, Stats];
    }));
    setStats(Object.fromEntries(entries));
  };

  const openCreate = () => { setForm({ ...emptyForm }); setEditingId(null); setShowForm(true); };
  const openEdit = (c: Campaign) => {
    setForm({
      key: c.key, name: c.name, headline: c.headline || '', subline: c.subline || '',
      benefitsText: (c.benefits || []).join('\n'), promoCode: c.promoCode || '',
      sortOrder: String(c.sortOrder || 0), isActive: !!c.isActive,
      giftProductId: c.giftProductId || '', adSpendHkd: c.adSpendCents !== null ? (c.adSpendCents / 100).toString() : '',
      liveAt: epochToInput(c.liveAt),
    });
    setEditingId(c.id);
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim() || (!editingId && !form.key.trim())) { setError('請填寫 Key 與名稱'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        key: form.key.trim().toLowerCase(), name: form.name.trim(), headline: form.headline.trim(),
        subline: form.subline.trim(), benefits: form.benefitsText.split('\n').map((s) => s.trim()).filter(Boolean),
        promoCode: form.promoCode.trim(), sortOrder: Number(form.sortOrder || 0), isActive: form.isActive,
        giftProductId: form.giftProductId ? Number(form.giftProductId) : null,
        adSpendCents: form.adSpendHkd !== '' ? Math.round((parseFloat(form.adSpendHkd) || 0) * 100) : null,
        liveAt: inputToEpoch(form.liveAt),
      };
      const res = await fetch(editingId ? `${API_BASE}/api/public/admin/lead-campaigns/${editingId}` : `${API_BASE}/api/public/admin/lead-campaigns`, {
        method: editingId ? 'PUT' : 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json; charset=utf-8' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '儲存失敗');
      setShowForm(false);
      setSuccess(editingId ? '活動已更新' : '活動已建立');
      await fetchCampaigns();
    } catch (e: any) { setError(e.message || '儲存失敗'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (c: Campaign) => {
    const res = await fetch(`${API_BASE}/api/public/admin/lead-campaigns/${c.id}/toggle`, { method: 'POST', headers: authHeaders() });
    if (res.ok) await fetchCampaigns(); else setError('切換失敗');
  };

  const remove = async (c: Campaign) => {
    if (!confirm(`確定刪除活動「${c.name}」？`)) return;
    const res = await fetch(`${API_BASE}/api/public/admin/lead-campaigns/${c.id}`, { method: 'DELETE', headers: authHeaders() });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { setError(data.error || '刪除失敗'); return; }
    setSuccess('已刪除');
    await fetchCampaigns();
  };

  const updateClaimStatus = async (id: number, status: string) => {
    const res = await apiFetch(`/api/public/admin/godfather/claims/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }),
    });
    if (res.ok) { setSuccess(`已更新為「${STATUS_LABELS[status]}」`); await fetchClaims(); }
    else setError('更新失敗');
  };

  const copyLandingLink = (c: Campaign) => {
    const origin = window.location.origin;
    const url = c.shortCode
      ? `${origin}/gf/${c.shortCode}`
      : `${origin}/godfather/${c.key}`;
    navigator.clipboard?.writeText(url).then(() => setSuccess('推廣短連結已複製 ✓')).catch(() => {});
  };

  const exportLabelCsv = () => {
    const list = filteredClaims;
    const rows = [['活動', '姓名', '地址行', '地區', '電話', '狀態', '推薦碼', '新會員', '領取時間']];
    const campName = (id: number | null) => campaigns.find((c) => c.id === id)?.name || '';
    for (const c of list) {
      rows.push([
        campName(c.campaign_id), c.name,
        c.address.replace(/\n/g, ' ').replace(/,/g, '，'),
        c.region === 'mainland' ? '內地' : '香港', c.phone,
        STATUS_LABELS[c.status] || c.status, c.referral_code || '',
        c.created_referrer ? '是' : '舊會員',
        new Date(c.created_at * 1000).toLocaleDateString('zh-HK'),
      ]);
    }
    const csv = '\uFEFF' + rows.map((r) => r.map((v) => `"${String(v)}"`).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lead-campaign-mailing-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredClaims = useMemo(
    () => claimsCampaignFilter === 'all' ? claims : claims.filter((c) => c.campaign_id === claimsCampaignFilter),
    [claims, claimsCampaignFilter]
  );

  // A4 14 上列印（99.1×38.1mm，2 欄×7 行）——列印目前篩選的名單
  const labelSheets = useMemo(() => {
    const chunks: Claim[][] = [];
    for (let i = 0; i < filteredClaims.length; i += 14) chunks.push(filteredClaims.slice(i, i + 14));
    return chunks;
  }, [filteredClaims]);

  const printLabels = () => {
    if (!filteredClaims.length) { setError('沒有可列印的記錄'); return; }
    setTimeout(() => window.print(), 250);
  };

  const inputCls = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white text-sm';
  const btn = 'px-4 py-2 rounded-lg font-medium transition';
  const tabBtn = (t: string, label: string, icon?: React.ReactNode) => (
    <button onClick={() => setTab(t as any)}
      className={`px-4 py-2 font-medium text-sm rounded-lg transition flex items-center gap-1.5 ${tab === t ? 'bg-red-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
      {icon}{label}
    </button>
  );

  return (
    <AdminLayout currentPage="godfather">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <Megaphone className="w-6 h-6 text-red-600" /> 推廣活動
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">群轉個人獲客活動——後台建活動、貼連結、看漏斗（對標食肆優惠模式）</p>
          </div>
          <button onClick={openCreate} className={`${btn} bg-red-600 text-white hover:bg-red-700 flex items-center gap-2`}>
            <Plus size={18} /> 新增活動
          </button>
        </div>

        {success && <div className="bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 p-3 rounded-lg text-sm">{success}</div>}
        {error && !showForm && <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 p-3 rounded-lg text-sm">{error}</div>}

        <div className="flex gap-2">
          {tabBtn('campaigns', '活動管理', <Megaphone size={16} />)}
          {tabBtn('claims', '領取名單', <Users size={16} />)}
          {tabBtn('stats', '成效報表', <BarChart3 size={16} />)}
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">載入中…</div>
        ) : (
          <>
            {/* TAB 1：活動管理 */}
            {tab === 'campaigns' && (campaigns.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-10 text-center text-gray-500 dark:text-gray-400">
                還沒有活動。點「新增活動」建立你的第一場群轉個人推廣。
              </div>
            ) : (
              <div className="space-y-3">
                {campaigns.map((c) => (
                  <div key={c.id} className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`font-bold ${c.isActive ? 'text-gray-800 dark:text-white' : 'text-gray-400 dark:text-gray-500 line-through'}`}>{c.name}</span>
                          <span className="text-xs font-mono text-gray-400">{c.key}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${c.isActive ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                            {c.isActive ? '進行中' : '已結束'}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">{c.claimCount} 人領取</span>
                        </div>
                        {c.headline && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{c.headline} — {c.subline}</p>}
                        <div className="flex items-center gap-3 mt-2 text-xs">
                          <span className="text-gray-500 dark:text-gray-400">優惠碼：<b className="font-mono text-red-600">{c.promoCode || '（未設）'}</b></span>
                          <button onClick={() => copyLandingLink(c)} className="text-blue-600 hover:underline flex items-center gap-1 dark:text-blue-400">
                            <Copy size={12} /> {c.shortCode ? `/gf/${c.shortCode}` : `/godfather/${c.key}`}
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => toggleActive(c)} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${c.isActive ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                          {c.isActive ? '進行中' : '已結束'}
                        </button>
                        <button onClick={() => openEdit(c)} className="p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400"><Edit2 size={18} /></button>
                        <button onClick={() => remove(c)} className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400"><Trash2 size={18} /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}

            {/* TAB 2：領取名單 */}
            {tab === 'claims' && (
              <>
                <div className="flex items-center justify-between gap-3">
                  <select value={String(claimsCampaignFilter)} onChange={(e) => setClaimsCampaignFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))} className={`${inputCls} w-auto`}>
                    <option value="all">全部活動</option>
                    {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <div className="flex gap-2">
                    <button onClick={fetchCampaigns} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 text-sm flex items-center gap-1.5"><RefreshCw size={14} /> 刷新</button>
                    <button onClick={exportLabelCsv} disabled={!filteredClaims.length} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 text-sm flex items-center gap-1.5 disabled:opacity-50"><Download size={14} /> CSV</button>
                    <button onClick={printLabels} disabled={!filteredClaims.length} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"><Printer size={14} /> 列印 Label（A4 14上）</button>
                  </div>
                </div>
                {!filteredClaims.length ? (
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-10 text-center text-gray-500 dark:text-gray-400">暫時未有領取記錄。</div>
                ) : (
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-stone-100 dark:bg-gray-700 text-left">
                        <tr>
                          <th className="px-4 py-3 font-medium">收件人</th>
                          <th className="px-4 py-3 font-medium">WhatsApp</th>
                          <th className="px-4 py-3 font-medium">地址</th>
                          <th className="px-4 py-3 font-medium">會員</th>
                          <th className="px-4 py-3 font-medium">來源</th>
                          <th className="px-4 py-3 font-medium">領取日</th>
                          <th className="px-4 py-3 font-medium">直播提醒</th>
                          <th className="px-4 py-3 font-medium">郵寄狀態</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {filteredClaims.map((c) => (
                          <tr key={c.id} className="hover:bg-stone-50 dark:hover:bg-gray-700/50">
                            <td className="px-4 py-3 font-medium text-gray-800 dark:text-white">{c.name}</td>
                            <td className="px-4 py-3 text-gray-600 dark:text-gray-400 font-mono text-xs">{c.phone}</td>
                            <td className="px-4 py-3 text-gray-600 dark:text-gray-400 max-w-xs">{c.address}</td>
                            <td className="px-4 py-3">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${c.created_referrer ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                                {c.created_referrer ? '新會員' : '舊會員'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {c.source === 'partner'
                                ? <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">夥伴</span>
                                : <span className="text-xs text-gray-400 dark:text-gray-500">禮遇</span>}
                            </td>
                            <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{new Date(c.created_at * 1000).toLocaleDateString('zh-HK')}</td>
                            <td className="px-4 py-3">
                              {c.reminder_sent
                                ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">已提醒</span>
                                : <span className="text-xs text-gray-300 dark:text-gray-600">—</span>}
                            </td>
                            <td className="px-4 py-3">
                              <select value={c.status} onChange={(e) => updateClaimStatus(c.id, e.target.value)}
                                className={`${inputCls} !py-1.5 !px-2 w-auto`}>
                                {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {/* TAB 3：成效報表 */}
            {tab === 'stats' && (
              <div className="space-y-6">
                {campaigns.filter((c) => stats[c.id]).map((c) => {
                  const s = stats[c.id];
                  const f = s.funnel;
                  const rate = (a: number, b: number) => (b > 0 ? `${((a / b) * 100).toFixed(1)}%` : '—');
                  return (
                    <div key={c.id} className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-bold text-gray-800 dark:text-white">{c.name} <span className="text-xs font-mono text-gray-400">{c.key}</span></h3>
                        <span className="text-xs text-gray-500 dark:text-gray-400">優惠碼 <b className="font-mono text-red-600">{c.promoCode || '—'}</b></span>
                      </div>
                      {/* 漏斗 */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        {[
                          { label: '落地頁瀏覽', value: f.views },
                          { label: '開始填表', value: f.formStarts, sub: rate(f.formStarts, f.views) },
                          { label: '提交成功', value: f.submitsOk, sub: rate(f.submitsOk, f.views) },
                          { label: '新會員', value: f.newMembers, sub: `佔提交 ${rate(f.newMembers, f.submitsOk)}` },
                        ].map((k) => (
                          <div key={k.label} className="bg-stone-50 dark:bg-gray-700/50 rounded-xl p-3">
                            <p className="text-xs text-gray-500 dark:text-gray-400">{k.label}</p>
                            <p className="text-2xl font-bold text-stone-800 dark:text-white mt-0.5">{k.value}</p>
                            {k.sub && <p className="text-xs text-red-600 mt-0.5">{k.sub}</p>}
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-xs">
                        <div className="text-gray-500 dark:text-gray-400">重複號碼（已領過）：{f.submitsDupPhone}</div>
                        <div className="text-gray-500 dark:text-gray-400">同網絡被擋：{f.submitsBlocked}</div>
                        <div className="text-gray-500 dark:text-gray-400">總領取記錄：{f.claims}</div>
                        <div className="text-gray-500 dark:text-gray-400">舊會員補領：{f.claims - f.newMembers}</div>
                      </div>
                      {/* 郵寄進度 */}
                      <div className="flex gap-2 flex-wrap mb-4">
                        {Object.entries(STATUS_LABELS).map(([st, label]) => (
                          <span key={st} className="text-xs px-3 py-1 rounded-full bg-stone-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                            {label}：<b>{s.mailing[st] || 0}</b>
                          </span>
                        ))}
                      </div>
                      {/* 商業成效（leads→買家→營收→CAC） */}
                      {s.commerce && (
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                          <p className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-2">💰 商業成效（該批會員至今）</p>
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            {[
                              { label: '轉化買家', value: String(s.commerce.buyers), sub: `領取→買家 ${s.commerce.leadToBuyerRate}` },
                              { label: '貢獻訂單', value: String(s.commerce.orders), sub: '' },
                              { label: '已確認營收', value: `HK$${(s.commerce.revenueCents / 100).toFixed(0)}`, sub: '' },
                              { label: '人均消費 LTV', value: `HK$${(s.commerce.revenuePerBuyerCents / 100).toFixed(0)}`, sub: '每位買家' },
                              { label: 'CAC', value: s.commerce.cacCents !== null ? `HK$${(s.commerce.cacCents / 100).toFixed(0)}` : '—', sub: s.commerce.adSpendCents > 0 ? `投放 HK$${(s.commerce.adSpendCents / 100).toFixed(0)}` : '未填投放成本' },
                            ].map((k) => (
                              <div key={k.label} className="bg-stone-50 dark:bg-gray-700/50 rounded-xl p-3">
                                <p className="text-xs text-gray-500 dark:text-gray-400">{k.label}</p>
                                <p className="text-xl font-bold text-stone-800 dark:text-white mt-0.5">{k.value}</p>
                                {k.sub && <p className="text-xs text-gray-400 mt-0.5">{k.sub}</p>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {!campaigns.length && <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-10 text-center text-gray-500 dark:text-gray-400">先建立活動，才有成效數據。</div>}
              </div>
            )}
          </>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto py-8 px-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800 dark:text-white">{editingId ? '編輯活動' : '新增活動'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">✕</button>
            </div>
            {error && <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 p-3 rounded-lg text-sm">{error}</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">活動 Key *</label>
                <input value={form.key} disabled={!!editingId} onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="例如：fb-q3-2026" className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">活動名稱 *</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="例如：Q3 群友禮遇" className={inputCls} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">落地頁主標</label>
              <input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} placeholder="群組專屬禮遇" className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">副標</label>
              <input value={form.subline} onChange={(e) => setForm({ ...form, subline: e.target.value })} placeholder="免費試吃裝直接郵寄到你屋企…" className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">賣點（每行一個，最多 3 行）</label>
              <textarea value={form.benefitsText} onChange={(e) => setForm({ ...form, benefitsText: e.target.value })} rows={3}
                placeholder={'免費試吃裝郵寄到家\n首單專屬折扣碼\n自動成為會員'} className={inputCls} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">專屬優惠碼</label>
                <input value={form.promoCode} onChange={(e) => setForm({ ...form, promoCode: e.target.value })} placeholder="WELCOME15" className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">排序</label>
                <input type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">贈品掛鉤產品<span className="text-xs text-gray-400 ml-1">（寄出時自動扣庫存）</span></label>
                <select value={String(form.giftProductId || '')} onChange={(e) => setForm({ ...form, giftProductId: e.target.value })} className={inputCls}>
                  <option value="">（不掛鉤）</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">投放成本（HKD，選填）<span className="text-xs text-gray-400 ml-1">（算 CAC 用）</span></label>
                <input type="number" min="0" step="0.1" value={form.adSpendHkd} onChange={(e) => setForm({ ...form, adSpendHkd: e.target.value })} placeholder="500" className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">直播開播時間（選填）<span className="text-xs text-gray-400 ml-1">（設了＝開播前 30 分鐘 WhatsApp 提醒自動分批）</span></label>
                <input type="datetime-local" value={form.liveAt} onChange={(e) => setForm({ ...form, liveAt: e.target.value })} className={inputCls} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} className="w-4 h-4 accent-red-600" />
              進行中（落地頁可領取）
            </label>
            <div className="flex justify-end gap-2 border-t border-gray-200 dark:border-gray-700 pt-4">
              <button onClick={() => setShowForm(false)} className={`${btn} border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300`}>取消</button>
              <button onClick={save} disabled={saving} className={`${btn} bg-red-600 text-white hover:bg-red-700 disabled:opacity-60`}>{saving ? '儲存中…' : '儲存'}</button>
            </div>
          </div>
        </div>
      )}
      {/* ══ A4 14上 Label 列印版面（螢幕隱藏，列印獨佔）══ */}
      <style>{`
        .print-root { display: none; }
        @media print {
          @page { size: A4 portrait; margin: 0; }
          body * { visibility: hidden; }
          .print-root, .print-root * { visibility: visible; }
          .print-root { display: block !important; position: absolute; left: 0; top: 0; }
          .label-sheet { width: 210mm; height: 296.5mm; padding: 15.1mm 5.9mm; display: grid; grid-template-columns: repeat(2, 99.1mm); grid-auto-rows: 38.1mm; box-sizing: border-box; }
          .label-sheet:not(:first-child) { break-before: page; }
          .label-cell { box-sizing: border-box; padding: 2.2mm 3mm; border: 0.35mm solid #9a9a9a; border-radius: 2.5mm; display: flex; gap: 2.5mm; overflow: hidden; background: #fff; }
          .lc-left { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; }
          .lc-brand { font-size: 9pt; font-weight: 900; color: #dc2626; letter-spacing: 0.3mm; white-space: nowrap; }
          .lc-brand small { font-size: 5.5pt; font-weight: 700; color: #555; letter-spacing: 0.5mm; margin-left: 1.5mm; }
          .lc-name { font-size: 12.5pt; font-weight: 900; color: #111; margin-top: 1.2mm; line-height: 1.15; }
          .lc-addr { font-size: 8.5pt; color: #222; line-height: 1.3; margin-top: 0.8mm; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
          .lc-cta { margin-top: auto; font-size: 6.6pt; color: #dc2626; font-weight: 700; line-height: 1.25; }
          .lc-right { flex: 0 0 24mm; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.8mm; border-left: 0.25mm dashed #bbb; padding-left: 2.2mm; }
          .lc-scan { font-size: 6pt; font-weight: 700; color: #333; text-align: center; line-height: 1.2; }
        }
      `}</style>
      <div className="print-root" aria-hidden="true">
        {labelSheets.map((sheet, si) => (
          <div className="label-sheet" key={si}>
            {sheet.map((c) => (
              <div className="label-cell" key={c.id}>
                <div className="lc-left">
                  <div className="lc-brand">{SITE_NAME}</div>
                  <div className="lc-name">{c.name}</div>
                  <div className="lc-addr">{c.address}</div>
                  <div className="lc-cta">✂️ 唔好扔——呢張係你嘅專屬邀請卡：掃碼逛店，朋友埋嚟落單你有回贈</div>
                </div>
                <div className="lc-right">
                  <QRCodeSVG
                    value={c.referral_code ? `${window.location.origin}/?r=${String(c.referral_code).replace(/^R-/, '')}` : `${window.location.origin}/store`}
                    size={76}
                    level="M"
                  />
                  <div className="lc-scan">專屬邀請<br />掃碼選購</div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </AdminLayout>
  );
};

export default AdminGodfather;
